import { addDoc, collection, getDocs, Timestamp } from "firebase/firestore";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useAuth } from "../hooks/useAuth";
import { db } from "../network/firebaseConfig";
import { server } from "../network/nodeServer";
import CustomSelect from "./common/CustomSelect";
// import { sendSms } from "../utils/sendSms";

const ComplaintForm = ({ onClose }) => {
    const { user } = useAuth();
    const [complaintCount, setComplaintCount] = useState(0);
    const [submitting, setSubmitting] = useState(false);

    const initialForm = {
        customerName: "",
        customerPhone: "",
        address: "",
        description: "",
        department: "",
        approximateResolveTime: "",
        onu: { power: "", pon: "", lan: "" },
        router: { power: "", wanLan: "", wifi: "" },
    };

    const [form, setForm] = useState(initialForm);

    const departments = ["Network", "Fiber", "Support"];
    const onuPowerOptions = ["on", "off"];
    const onuPONOptions = ["red", "green", "blinking", "no light"];
    const onuLANOptions = ["on", "off"];
    const routerPowerOptions = ["on", "off"];
    const routerWANLANOptions = ["off", "red", "green", "green blinking"];
    const routerWifiOptions = ["Name changed", "same as previous", "connected no internet", "not connecting"];


    // Count existing complaints
    useEffect(() => {
        const fetchCount = async () => {
            try {
                const snapshot = await getDocs(collection(db, "complaints"));
                setComplaintCount(snapshot.size);
            } catch (err) {
                console.error(err);
            }
        };
        fetchCount();
    }, []);

    // Handle normal input
    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    // Handle nested select changes (onu/router)
    const handleSelectChange = (name, value, group = null) => {
        if (group) {
            setForm((prev) => ({
                ...prev,
                [group]: { ...prev[group], [name]: value },
            }));
        } else {
            setForm((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.customerName || !form.customerPhone) {
            return toast.error("Please fill all required fields");
        }

        try {
            setSubmitting(true);
            const ticketNumber = `TICKET-${complaintCount + 1}`;

            // 1️⃣ Add complaint to Firestore and capture the new document reference
            const docRef = await addDoc(collection(db, "complaints"), {
                customerInfo: {
                    name: form.customerName,
                    phone: form.customerPhone,
                    address: form.address,
                },
                department: form.department,
                description: form.description,
                approximateResolveTime: form.approximateResolveTime,
                onu: form.onu,
                router: form.router,
                status: "pending",
                ticketNumber,
                createdBy: user.uid,
                createdAt: Timestamp.now(),
                activityLogs: [
                    {
                        time: Timestamp.now(),
                        message: "Complaint created by Helpdesk",
                        userId: user.uid,
                    },
                ],
            });

            // 2️⃣ Create complaint tracking URL
            const complaintUrl = `${window.location.origin}/complaint/${docRef.id}`;

            toast.success(`Complaint submitted successfully (Ticket: ${ticketNumber})`);
            onClose?.();
            setForm(initialForm);
            setComplaintCount((prev) => prev + 1);

            // 3️⃣ Bangla message with link
            const banglaMessage = `
প্রিয় ${form.customerName},

আপনার অভিযোগটি আমাদের সিস্টেমে গৃহীত হয়েছে।
আপনি নিচের লিঙ্ক থেকে অভিযোগের অবস্থা জানতে পারবেন:

${complaintUrl}

ধন্যবাদান্তে,
বিজয় আইটি সাপোর্ট টিম
`;

            // 4️⃣ Send SMS
            if (form.customerPhone) {
                // const result = await sendSms({
                //     number: form.customerPhone.startsWith("88")
                //         ? form.customerPhone
                //         : `88${form.customerPhone}`,
                //     message: banglaMessage,
                // });

                // const result = await fetch(`${server}/sms/send-sms`, {
                //     method: "POST",
                //     headers: { "Content-Type": "application/json" },
                //     body: JSON.stringify({
                //         number: form.customerPhone.startsWith("88")
                //             ? form.customerPhone
                //             : `88${form.customerPhone}`,
                //         message: banglaMessage,
                //     }),
                // });


                const result = await fetch("https://us-central1-ispadmin-7fb7e.cloudfunctions.net/sendSms", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        number: form.customerPhone.startsWith("88") ? form.customerPhone : `88${form.customerPhone}`,
                        message: banglaMessage,
                    }),
                });

                const data = await result.json(); // read the body **once**
                console.log("📩 SMS sent:", data);
            }

        } catch (err) {
            console.error(err);
            toast.error("Error submitting complaint");
        } finally {
            setSubmitting(false);
        }
    };


    // Inside your component
    const resolveTimeOptions = Array.from({ length: 24 }, (_, i) => `${i + 1} hour${i === 0 ? "" : "s"}`);

    return (
        <div className="max-w-4xl mx-auto bg-white p-6 rounded shadow space-y-6">
            <h2 className="text-xl font-semibold mb-4">New Complaint</h2>
            <form onSubmit={handleSubmit} className="space-y-6">

                {/* ONU Status */}
                <div className="border-t pt-4 space-y-4">
                    <h3 className="font-semibold text-lg mb-2">Customer Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block font-medium mb-1">Customer Name</label>
                            <input
                                type="text"
                                name="customerName"
                                value={form.customerName}
                                onChange={handleChange}
                                className="w-full border p-2 rounded focus:outline-1 outline-gray-300"
                                required
                            />
                        </div>
                        <div>
                            <label className="block font-medium mb-1">Customer Phone</label>
                            <input
                                type="text"
                                name="customerPhone"
                                value={form.customerPhone}
                                onChange={handleChange}
                                className="w-full border p-2 rounded focus:outline-1 outline-gray-300"
                                required
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block font-medium mb-1">Address</label>
                        <textarea
                            name="address"
                            value={form.address}
                            onChange={handleChange}
                            className="w-full border p-2 rounded focus:outline-1 outline-gray-300"
                        />
                    </div>
                </div>

                {/* ONU Status */}
                <div className="border-t pt-4 space-y-4">
                    <h3 className="font-semibold text-lg mb-2">Office Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block font-medium mb-1">Department</label>
                            <CustomSelect
                                name="department"
                                placeholder="Select Department"
                                options={departments}
                                value={form.department}
                                onChange={handleSelectChange}
                            />
                        </div>

                        <div>
                            <label className="block font-medium mb-1">Approximate Resolve Time</label>
                            <CustomSelect
                                name="approximateResolveTime"
                                placeholder="Select Time"
                                options={resolveTimeOptions}
                                value={form.approximateResolveTime}
                                onChange={handleSelectChange}
                            />
                        </div>
                    </div>
                </div>

                {/* ONU Status */}
                <div className="border-t pt-4 space-y-4">
                    <h3 className="font-semibold text-lg mb-2">ONU Status</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block font-medium mb-1">Power</label>
                            <CustomSelect
                                name="power"
                                placeholder="ONU Power"
                                options={onuPowerOptions}
                                value={form.onu.power}
                                onChange={(name, value) => handleSelectChange(name, value, "onu")}
                            />
                        </div>
                        <div>
                            <label className="block font-medium mb-1">PON</label>
                            <CustomSelect
                                name="pon"
                                placeholder="ONU PON"
                                options={onuPONOptions}
                                value={form.onu.pon}
                                onChange={(name, value) => handleSelectChange(name, value, "onu")}
                            />
                        </div>
                        <div>
                            <label className="block font-medium mb-1">LAN</label>
                            <CustomSelect
                                name="lan"
                                placeholder="ONU LAN"
                                options={onuLANOptions}
                                value={form.onu.lan}
                                onChange={(name, value) => handleSelectChange(name, value, "onu")}
                            />
                        </div>
                    </div>
                </div>

                {/* Router Status */}
                <div className="border-t pt-4 space-y-4">
                    <h3 className="font-semibold text-lg mb-2">Router Status</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block font-medium mb-1">Power</label>
                            <CustomSelect
                                name="power"
                                placeholder="Router Power"
                                options={routerPowerOptions}
                                value={form.router.power}
                                onChange={(name, value) => handleSelectChange(name, value, "router")}
                            />
                        </div>
                        <div>
                            <label className="block font-medium mb-1">WAN/LAN</label>
                            <CustomSelect
                                name="wanLan"
                                placeholder="Router WAN/LAN"
                                options={routerWANLANOptions}
                                value={form.router.wanLan}
                                onChange={(name, value) => handleSelectChange(name, value, "router")}
                            />
                        </div>
                        <div>
                            <label className="block font-medium mb-1">Wifi</label>
                            <CustomSelect
                                name="wifi"
                                placeholder="Router Wifi"
                                options={routerWifiOptions}
                                value={form.router.wifi}
                                onChange={(name, value) => handleSelectChange(name, value, "router")}
                            />
                        </div>
                    </div>
                </div>

                <div>
                    <label className="block font-medium mb-1">Description</label>
                    <textarea
                        name="description"
                        value={form.description}
                        onChange={handleChange}
                        className="w-full border p-2 rounded focus:outline-1 outline-gray-300"
                    />
                </div>

                <div className="flex">
                    <div className="flex gap-3">
                        <button type="submit"
                            className="px-3 bg-pink-500 hover:pink-red-600 text-white text-sm py-1 rounded-md"
                            disabled={submitting}>
                            Submit Complaint
                        </button>

                        <button
                            className="px-5 bg-gray-300 hover:pink-red-600 text-gray-900 text-sm py-1 rounded-md"
                            disabled={submitting}
                            onClick={onClose}
                        >
                            Cancel
                        </button>
                    </div>
                    <div className="flex"></div>
                </div>
            </form>
        </div>
    );
};

export default ComplaintForm;

// import { addDoc, collection, getDocs, Timestamp } from "firebase/firestore";
// import { useEffect, useState } from "react";
// import toast from "react-hot-toast";
// import { useAuth } from "../hooks/useAuth";
// import { db } from "../network/firebaseConfig";
// import CustomSelect from "./common/CustomSelect";
// import { smsSender } from "../utils/smsSender";

// const ComplaintForm = ({ onClose }) => {
//     const { user } = useAuth();
//     // const [technicians, setTechnicians] = useState([]);
//     const [complaintCount, setComplaintCount] = useState(0);
//     const [submitting, setSubmitting] = useState(false);

//     const initialForm = {
//         customerName: "",
//         customerPhone: "",
//         address: "",
//         // issueType: "",
//         description: "",
//         department: "",
//         // assignedTo: "",
//         approximateResolveTime: "",
//         onu: { power: "", pon: "", lan: "" },
//         router: { power: "", wanLan: "", wifi: "" },
//     };

//     const [form, setForm] = useState(initialForm);

//     const departments = ["Network", "Fiber", "Support"];
//     const onuPowerOptions = ["on", "off"];
//     const onuPONOptions = ["red", "green", "blinking", "no light"];
//     const onuLANOptions = ["on", "off"];
//     const routerPowerOptions = ["on", "off"];
//     const routerWANLANOptions = ["off", "red", "green", "green blinking"];
//     const routerWifiOptions = ["Name changed", "same as previous", "connected no internet", "not connecting"];


//     // Count existing complaints
//     useEffect(() => {
//         const fetchCount = async () => {
//             try {
//                 const snapshot = await getDocs(collection(db, "complaints"));
//                 setComplaintCount(snapshot.size);
//             } catch (err) {
//                 console.error(err);
//             }
//         };
//         fetchCount();
//     }, []);

//     // Handle normal input
//     const handleChange = (e) => {
//         const { name, value } = e.target;
//         setForm((prev) => ({ ...prev, [name]: value }));
//     };

//     // Handle nested select changes (onu/router)
//     const handleSelectChange = (name, value, group = null) => {
//         if (group) {
//             setForm((prev) => ({
//                 ...prev,
//                 [group]: { ...prev[group], [name]: value },
//             }));
//         } else {
//             setForm((prev) => ({ ...prev, [name]: value }));
//         }
//     };

//     const handleSubmit = async (e) => {
//         e.preventDefault();
//         if (!form.customerName || !form.customerPhone || !form.description) {
//             return toast.error("Please fill all required fields");
//         }

//         try {
//             setSubmitting(true);
//             const ticketNumber = `TICKET-${complaintCount + 1}`;

//             await addDoc(collection(db, "complaints"), {
//                 customerInfo: {
//                     name: form.customerName,
//                     phone: form.customerPhone,
//                     address: form.address,
//                 },
//                 department: form.department,
//                 // issueType: form.issueType,
//                 description: form.description,
//                 // assignedTo: form.assignedTo,
//                 approximateResolveTime: form.approximateResolveTime,
//                 onu: form.onu,
//                 router: form.router,
//                 status: "pending",
//                 ticketNumber,
//                 createdBy: user.uid,
//                 createdAt: Timestamp.now(),
//                 activityLogs: [
//                     {
//                         time: Timestamp.now(),
//                         message: "Complaint created by Helpdesk",
//                         userId: user.uid,
//                     },
//                 ],
//             });

//             toast.success(`Complaint submitted successfully (Ticket: ${ticketNumber})`);
//             onClose?.();
//             setForm(initialForm);
//             setComplaintCount((prev) => prev + 1);

//             const message = `Dear ${form.customerName},
// Your complaint is recorded. you will be notify shortly.
// Thank you for staying with Bijoy IT.
// — BijoyIT Support`

//             if (form.customerPhone) {
//                 smsSender(form.customerPhone, message)
//             }

//         } catch (err) {
//             console.error(err);
//             toast.error("Error submitting complaint");
//         } finally {
//             setSubmitting(false);
//         }
//     };

//     // Inside your component
//     const resolveTimeOptions = Array.from({ length: 24 }, (_, i) => `${i + 1} hour${i === 0 ? "" : "s"}`);

//     return (
//         <div className="max-w-4xl mx-auto bg-white p-6 rounded shadow space-y-6">
//             <h2 className="text-xl font-semibold mb-4">New Complaint</h2>
//             <form onSubmit={handleSubmit} className="space-y-6">

//                 {/* ONU Status */}
//                 <div className="border-t pt-4 space-y-4">
//                     <h3 className="font-semibold text-lg mb-2">Customer Information</h3>
//                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                         <div>
//                             <label className="block font-medium mb-1">Customer Name</label>
//                             <input
//                                 type="text"
//                                 name="customerName"
//                                 value={form.customerName}
//                                 onChange={handleChange}
//                                 className="w-full border p-2 rounded focus:outline-1 outline-gray-300"
//                                 required
//                             />
//                         </div>
//                         <div>
//                             <label className="block font-medium mb-1">Customer Phone</label>
//                             <input
//                                 type="text"
//                                 name="customerPhone"
//                                 value={form.customerPhone}
//                                 onChange={handleChange}
//                                 className="w-full border p-2 rounded focus:outline-1 outline-gray-300"
//                                 required
//                             />
//                         </div>
//                     </div>
//                     <div>
//                         <label className="block font-medium mb-1">Address</label>
//                         <textarea
//                             name="address"
//                             value={form.address}
//                             onChange={handleChange}
//                             className="w-full border p-2 rounded focus:outline-1 outline-gray-300"
//                         />
//                     </div>
//                 </div>

//                 {/* ONU Status */}
//                 <div className="border-t pt-4 space-y-4">
//                     <h3 className="font-semibold text-lg mb-2">Office Information</h3>
//                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                         <div>
//                             <label className="block font-medium mb-1">Department</label>
//                             <CustomSelect
//                                 name="department"
//                                 placeholder="Select Department"
//                                 options={departments}
//                                 value={form.department}
//                                 onChange={handleSelectChange}
//                             />
//                         </div>

//                         {/* <div>
//                             <label className="block font-medium mb-1">Assign To Technician</label>
//                             <CustomSelect
//                                 name="assignedTo"
//                                 placeholder="Select Technician"
//                                 options={technicians.map((t) => ({ label: `${t.name} (${t.department})`, value: t.uid }))}
//                                 value={form.assignedTo}
//                                 onChange={handleSelectChange}
//                             />
//                         </div> */}
//                         {/* <div>
//                             <label className="block font-medium mb-1">Approximate Resolve Time</label>
//                             <input
//                                 type="text"
//                                 name="approximateResolveTime"
//                                 value={form.approximateResolveTime}
//                                 onChange={handleChange}
//                                 className="w-full border p-2 rounded"
//                             />
//                         </div> */}

//                         <div>
//                             <label className="block font-medium mb-1">Approximate Resolve Time</label>
//                             <CustomSelect
//                                 name="approximateResolveTime"
//                                 placeholder="Select Time"
//                                 options={resolveTimeOptions}
//                                 value={form.approximateResolveTime}
//                                 onChange={handleSelectChange}
//                             />
//                         </div>
//                     </div>
//                 </div>

//                 {/* ONU Status */}
//                 <div className="border-t pt-4 space-y-4">
//                     <h3 className="font-semibold text-lg mb-2">ONU Status</h3>
//                     <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//                         <div>
//                             <label className="block font-medium mb-1">Power</label>
//                             <CustomSelect
//                                 name="power"
//                                 placeholder="ONU Power"
//                                 options={onuPowerOptions}
//                                 value={form.onu.power}
//                                 onChange={(name, value) => handleSelectChange(name, value, "onu")}
//                             />
//                         </div>
//                         <div>
//                             <label className="block font-medium mb-1">PON</label>
//                             <CustomSelect
//                                 name="pon"
//                                 placeholder="ONU PON"
//                                 options={onuPONOptions}
//                                 value={form.onu.pon}
//                                 onChange={(name, value) => handleSelectChange(name, value, "onu")}
//                             />
//                         </div>
//                         <div>
//                             <label className="block font-medium mb-1">LAN</label>
//                             <CustomSelect
//                                 name="lan"
//                                 placeholder="ONU LAN"
//                                 options={onuLANOptions}
//                                 value={form.onu.lan}
//                                 onChange={(name, value) => handleSelectChange(name, value, "onu")}
//                             />
//                         </div>
//                     </div>
//                 </div>

//                 {/* Router Status */}
//                 <div className="border-t pt-4 space-y-4">
//                     <h3 className="font-semibold text-lg mb-2">Router Status</h3>
//                     <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//                         <div>
//                             <label className="block font-medium mb-1">Power</label>
//                             <CustomSelect
//                                 name="power"
//                                 placeholder="Router Power"
//                                 options={routerPowerOptions}
//                                 value={form.router.power}
//                                 onChange={(name, value) => handleSelectChange(name, value, "router")}
//                             />
//                         </div>
//                         <div>
//                             <label className="block font-medium mb-1">WAN/LAN</label>
//                             <CustomSelect
//                                 name="wanLan"
//                                 placeholder="Router WAN/LAN"
//                                 options={routerWANLANOptions}
//                                 value={form.router.wanLan}
//                                 onChange={(name, value) => handleSelectChange(name, value, "router")}
//                             />
//                         </div>
//                         <div>
//                             <label className="block font-medium mb-1">Wifi</label>
//                             <CustomSelect
//                                 name="wifi"
//                                 placeholder="Router Wifi"
//                                 options={routerWifiOptions}
//                                 value={form.router.wifi}
//                                 onChange={(name, value) => handleSelectChange(name, value, "router")}
//                             />
//                         </div>
//                     </div>
//                 </div>

//                 <div>
//                     <label className="block font-medium mb-1">Description</label>
//                     <textarea
//                         name="description"
//                         value={form.description}
//                         onChange={handleChange}
//                         className="w-full border p-2 rounded focus:outline-1 outline-gray-300"
//                         required
//                     />
//                 </div>

//                 <div className="flex">
//                     <div className="flex gap-3">
//                         <button type="submit"
//                             className="px-3 bg-pink-500 hover:pink-red-600 text-white text-sm py-1 rounded-md"
//                             disabled={submitting}>
//                             Submit Complaint
//                         </button>

//                         <button
//                             className="px-5 bg-gray-300 hover:pink-red-600 text-gray-900 text-sm py-1 rounded-md"
//                             disabled={submitting}
//                             onClick={onClose}
//                         >
//                             Cancel
//                         </button>
//                     </div>
//                     <div className="flex"></div>
//                 </div>
//             </form>
//         </div>
//     );
// };

// export default ComplaintForm;





// import React, { useState } from "react";
// import { addDoc, collection, serverTimestamp } from "firebase/firestore";
// import { db, auth } from "../network/firebaseConfig";
// import toast from "react-hot-toast";

// const ComplainForm = () => {
//     const [loading, setLoading] = useState(false);

//     const [form, setForm] = useState({
//         creatorRole: "",
//         assignedRole: "",
//         monitorType: "",
//         customerName: "",
//         customerContact: "",
//         customerAddress: "",
//         issueDetails: "",
//         onuStatus: {
//             power: "",
//             pon: "",
//             lan: "",
//         },
//         onuSerial: "",
//         status: "Pending",
//         resolutionComment: "",
//     });

//     const handleChange = (e) => {
//         const { name, value } = e.target;
//         setForm((prev) => ({ ...prev, [name]: value }));
//     };

//     const handleOnuChange = (e) => {
//         const { name, value } = e.target;
//         setForm((prev) => ({
//             ...prev,
//             onuStatus: { ...prev.onuStatus, [name]: value },
//         }));
//     };

//     const handleSubmit = async (e) => {
//         e.preventDefault();
//         setLoading(true);

//         try {
//             const user = auth.currentUser;
//             if (!user) throw new Error("User not logged in");

//             const complaintData = {
//                 creatorId: user.uid,
//                 creatorName: user.displayName || "Unknown",
//                 creatorRole: form.creatorRole,
//                 assignedRole: form.assignedRole,
//                 monitorType: form.monitorType,
//                 customerName: form.customerName,
//                 customerContact: form.customerContact,
//                 customerAddress: form.customerAddress,
//                 issueDetails: form.issueDetails,
//                 onuStatus: form.onuStatus,
//                 onuSerial: form.onuSerial,
//                 status: form.status,
//                 resolutionComment: form.status === "Solved" ? form.resolutionComment : "",
//                 createdAt: serverTimestamp(),
//                 updatedAt: serverTimestamp(),
//             };

//             await addDoc(collection(db, "complaints"), complaintData);
//             toast.success("Complaint submitted successfully!");
//             setForm({
//                 creatorRole: "",
//                 assignedRole: "",
//                 monitorType: "",
//                 customerName: "",
//                 customerContact: "",
//                 customerAddress: "",
//                 issueDetails: "",
//                 onuStatus: { power: "", pon: "", lan: "" },
//                 onuSerial: "",
//                 status: "Pending",
//                 resolutionComment: "",
//             });
//         } catch (error) {
//             console.error(error);
//             toast.error(error.message);
//         } finally {
//             setLoading(false);
//         }
//     };

//     return (
//         <div className="complain-body p-6 bg-gray-100 min-h-screen">
//             <div className="complain-container bg-white shadow rounded-lg p-6 max-w-3xl mx-auto">
//                 <h2 className="text-2xl font-semibold mb-4 text-center">Complain Form</h2>

//                 <form onSubmit={handleSubmit} className="space-y-5">
//                     {/* Creator & Assigned Role */}
//                     <div className="grid grid-cols-2 gap-4">
//                         <div>
//                             <label>Complain Creator Role</label>
//                             <select
//                                 name="creatorRole"
//                                 value={form.creatorRole}
//                                 onChange={handleChange}
//                                 required
//                                 className="w-full border p-2 rounded"
//                             >
//                                 <option value="">Select</option>
//                                 <option>Helpdesk</option>
//                                 <option>Distributor</option>
//                             </select>
//                         </div>

//                         <div>
//                             <label>Assigned Role</label>
//                             <select
//                                 name="assignedRole"
//                                 value={form.assignedRole}
//                                 onChange={handleChange}
//                                 required
//                                 className="w-full border p-2 rounded"
//                             >
//                                 <option value="">Select</option>
//                                 <option>Operation</option>
//                                 <option>Technical</option>
//                             </select>
//                         </div>
//                     </div>

//                     {/* Monitor */}
//                     <div>
//                         <label>User / Status Monitoring</label>
//                         <select
//                             name="monitorType"
//                             value={form.monitorType}
//                             onChange={handleChange}
//                             required
//                             className="w-full border p-2 rounded"
//                         >
//                             <option value="">Select</option>
//                             <option>User</option>
//                             <option>Status Monitoring</option>
//                         </select>
//                     </div>

//                     <hr className="my-4" />

//                     {/* Customer Details */}
//                     <h3 className="font-semibold text-lg mb-2">Customer Details</h3>
//                     <div className="grid grid-cols-2 gap-4">
//                         <div>
//                             <label>Customer Name</label>
//                             <input
//                                 type="text"
//                                 name="customerName"
//                                 value={form.customerName}
//                                 onChange={handleChange}
//                                 required
//                                 className="w-full border p-2 rounded"
//                             />
//                         </div>
//                         <div>
//                             <label>Contact Number</label>
//                             <input
//                                 type="text"
//                                 name="customerContact"
//                                 value={form.customerContact}
//                                 onChange={handleChange}
//                                 required
//                                 className="w-full border p-2 rounded"
//                             />
//                         </div>
//                     </div>

//                     <div>
//                         <label>Address</label>
//                         <textarea
//                             name="customerAddress"
//                             value={form.customerAddress}
//                             onChange={handleChange}
//                             required
//                             className="w-full border p-2 rounded"
//                         />
//                     </div>

//                     <hr className="my-4" />

//                     {/* ONU Status */}
//                     <h3 className="font-semibold text-lg mb-2">ONU Status</h3>
//                     <div className="grid grid-cols-3 gap-4">
//                         <div>
//                             <label>Power</label>
//                             <select
//                                 name="power"
//                                 value={form.onuStatus.power}
//                                 onChange={handleOnuChange}
//                                 required
//                                 className="w-full border p-2 rounded"
//                             >
//                                 <option value="">Select</option>
//                                 <option>On</option>
//                                 <option>Off</option>
//                             </select>
//                         </div>

//                         <div>
//                             <label>PON</label>
//                             <select
//                                 name="pon"
//                                 value={form.onuStatus.pon}
//                                 onChange={handleOnuChange}
//                                 required
//                                 className="w-full border p-2 rounded"
//                             >
//                                 <option value="">Select</option>
//                                 <option>Green</option>
//                                 <option>Red</option>
//                                 <option>Blinking</option>
//                                 <option>No Light</option>
//                             </select>
//                         </div>

//                         <div>
//                             <label>LAN</label>
//                             <select
//                                 name="lan"
//                                 value={form.onuStatus.lan}
//                                 onChange={handleOnuChange}
//                                 required
//                                 className="w-full border p-2 rounded"
//                             >
//                                 <option value="">Select</option>
//                                 <option>On</option>
//                                 <option>Off</option>
//                             </select>
//                         </div>
//                     </div>

//                     {/* Issue Details */}
//                     <div>
//                         <label>Issue Details</label>
//                         <textarea
//                             name="issueDetails"
//                             value={form.issueDetails}
//                             onChange={handleChange}
//                             required
//                             className="w-full border p-2 rounded"
//                         />
//                     </div>

//                     <div>
//                         <label>ONU Serial (optional)</label>
//                         <input
//                             type="text"
//                             name="onuSerial"
//                             value={form.onuSerial}
//                             onChange={handleChange}
//                             className="w-full border p-2 rounded"
//                         />
//                     </div>

//                     <hr className="my-4" />

//                     {/* Status */}
//                     <div>
//                         <label>Complain Status</label>
//                         <select
//                             name="status"
//                             value={form.status}
//                             onChange={handleChange}
//                             className="w-full border p-2 rounded"
//                         >
//                             <option>Pending</option>
//                             <option>In Operation</option>
//                             <option>Solved</option>
//                         </select>
//                     </div>

//                     {form.status === "Solved" && (
//                         <div>
//                             <label>Resolution Comment</label>
//                             <textarea
//                                 name="resolutionComment"
//                                 value={form.resolutionComment}
//                                 onChange={handleChange}
//                                 className="w-full border p-2 rounded"
//                             />
//                         </div>
//                     )}

//                     <button
//                         type="submit"
//                         disabled={loading}
//                         className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 w-full"
//                     >
//                         {loading ? "Submitting..." : "Submit Complaint"}
//                     </button>
//                 </form>
//             </div>
//         </div>
//     );
// };

// export default ComplainForm;


// import React, { useState } from 'react';
// import toast from 'react-hot-toast';
// import "../../src/styles/ComplainForm.css"

// const ComplainPage = () => {
//     const [form, setForm] = useState({
//         creator: '',
//         role: '',
//         monitor: '',
//         name: '',
//         details: '',
//         power: '',
//         pon: '',
//         lan: '',
//         contact: '',
//         address: '',
//         status: 'Pending',
//         sn: '',
//         comment: '',
//     });

//     const handleChange = (e) => {
//         const { name, value } = e.target;
//         setForm({ ...form, [name]: value });
//     };

//     const handleSubmit = (e) => {
//         e.preventDefault();
//         toast.success('Complain submitted!');
//         console.log(form);
//     };

//     return (
//         <div className="complain-body">
//             <div className="complain-container">
//                 <h2>Complain Form</h2>
//                 <form onSubmit={handleSubmit} className="complain-form">

//                     {/* Top Selectors */}
//                     <div className="form-row">
//                         <div className="form-group half-width">
//                             <label>Complain Creator/Distributor</label>
//                             <select name="creator" value={form.creator} onChange={handleChange} required>
//                                 <option value="">Select</option>
//                                 <option>Creator</option>
//                                 <option>Distributor</option>
//                             </select>
//                         </div>

//                         <div className="form-group half-width">
//                             <label>Operation/Technical Person/Status Changer</label>
//                             <select name="role" value={form.role} onChange={handleChange} required>
//                                 <option value="">Select</option>
//                                 <option>Operation</option>
//                                 <option>Technical</option>
//                                 <option>Status Changer</option>
//                             </select>
//                         </div>
//                     </div>

//                     <div className="form-group">
//                         <label>User/Status Monitoring</label>
//                         <select name="monitor" value={form.monitor} onChange={handleChange} required>
//                             <option value="">Select</option>
//                             <option>User</option>
//                             <option>Status Monitoring</option>
//                         </select>
//                     </div>

//                     <hr className='h-2 bg-gray-300' />

//                     <label>Customer Details</label>
//                     <div className="form-row">
//                         <div className="form-group half-width">
//                             <label>Name/Username</label>
//                             <input type="text" name="name" value={form.name} onChange={handleChange} required
//                                 className='focus:outline-1 outline-gray-300' />
//                         </div>

//                         <div className="form-group half-width">
//                             <label>Contact No</label>
//                             <input type="text" name="contact" value={form.contact} onChange={handleChange} required
//                                 className='focus:outline-1 outline-gray-300'
//                             />
//                         </div>
//                     </div>

//                     <div className="form-group">
//                         <label>Address</label>
//                         <textarea name="address" value={form.address} onChange={handleChange} required
//                             className='focus:outline-dashed outline-gray-400' />
//                     </div>

//                     <hr />

//                     <h3>ONU Status</h3>
//                     <div className="grid grid-cols-3 gap-3">
//                         <div className="form-group half-width">
//                             <label>Power</label>
//                             <select name="power" value={form.power} onChange={handleChange} required>
//                                 <option value="">Select</option>
//                                 <option>On</option>
//                                 <option>Off</option>
//                             </select>
//                         </div>

//                         <div className="form-group half-width">
//                             <label>PON</label>
//                             <select name="pon" value={form.pon} onChange={handleChange} required>
//                                 <option value="">Select</option>
//                                 <option>Red</option>
//                                 <option>Green</option>
//                                 <option>Blinking</option>
//                                 <option>No Light</option>
//                             </select>
//                         </div>

//                         <div className="form-group">
//                             <label>LAN</label>
//                             <select name="lan" value={form.lan} onChange={handleChange} required>
//                                 <option value="">Select</option>
//                                 <option>On</option>
//                                 <option>Off</option>
//                             </select>
//                         </div>

//                     </div>

//                     <div className="form-group">
//                         <label>Details Explaination</label>
//                         {/* <input type="text" name="details" value={form.details} onChange={handleChange} required /> */}
//                         <textarea name="details" value={form.details} onChange={handleChange} required
//                             className='focus:outline-dashed outline-gray-400' />
//                     </div>

//                     <hr className='my-6 h-1 bg-gray-300' />

//                     <div className="form-group">
//                         <label>Complain Status</label>
//                         <select name="status" value={form.status} onChange={handleChange}>
//                             <option>Pending</option>
//                             <option>In Operation</option>
//                             <option>Solved</option>
//                         </select>
//                     </div>

//                     {form.status === 'Solved' && (
//                         <div className="form-group">
//                             <label>Comment</label>
//                             <textarea name="comment" value={form.comment} onChange={handleChange} className='focus:outline-1 outline-gray-300' />
//                         </div>
//                     )}

//                     <button type="submit" className="submit-btn">Submit</button>
//                 </form>
//             </div>
//         </div>
//     );
// };

// export default ComplainPage;